import { Injectable, Inject } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { BehaviorSubject } from "rxjs";
import { ApiConfiguration } from "../../api/api-configuration";
import { AuthApiService } from "../core-rms/auth.service";
import { FileUploadConstants } from "./file-upload.constants";
import { LookupService } from "../../../../shared/core/services/lookup.service";
import { map } from "rxjs/operators";
import { RmsAccessService } from "./rms-access.service";
import { NdmsSafeSubscriber } from "../../../../shared/core/interfaces/ndms-safe-subscriber";
import { LookupManagerService } from "./../../api/services/lookup-manager.service";
import { ResponderDocumentService } from "./../../api/services/responder-document.service";
import { FileUploadResult } from "./file-upload-result";
import * as moment from "moment";
import { LookupValueModel } from "../../api/models/lookup-value-model";

@Injectable({
  providedIn: "root"
})
export abstract class FileService extends NdmsSafeSubscriber {

  constructor(@Inject("BASE_URL") protected baseUrl: string,
    protected http: HttpClient,
    protected lookupManagerService: LookupManagerService,
    protected config: ApiConfiguration,
    protected authService: AuthApiService,
    protected lookupService: LookupService,
    protected rmsAccessService: RmsAccessService,
    protected responderDocumentService: ResponderDocumentService,
    protected fileuploadConstants: FileUploadConstants) {
    super();
    FileService.updateMessage(false);
    this.rootUrl = baseUrl;
  }

  docTypes: LookupValueModel[] = [];
  rootUrl = "";

  loadLookups() {
    return new Promise<void>((resolve, reject) => {
      this.safeSubscribe(this.lookupManagerService.GetLookups(["DocumentTypes"]),
        (res: any[]) => {
          this.lookupService.loadLookups(res);
          resolve();
        },
        response => reject(response));
    });
  }

  private static message = new BehaviorSubject<boolean>(false);

  static getMessage(): Observable<boolean> {
    return this.message.asObservable();
  }

  static updateMessage(message: boolean): void {
    this.message.next(message);
  }

  canUploadDocuments(): boolean {
    return (this.rmsAccessService.hasPermission("Documents:Upload") === "Documents:Upload");
  }

  canDeleteDocuments() {
    return (this.rmsAccessService.hasPermission("Documents:Delete") === "Documents:Delete");
  }

  exportData(file) {
    const url = (file) ? `${this.baseUrl}${this.fileuploadConstants.RESPONDER_DOCUMENTS_API_PATH}DownloadFile/${file.responderDocumentId}` : `${this.baseUrl}${this.fileuploadConstants.RESPONDER_DOCUMENTS_API_PATH}DownloadFile`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' });
  }

  upload(url: string, formData: any): Observable<any> {
    const reqUrl = this.rootUrl + url;
    const headers = new HttpHeaders({ 'Authorization': this.authService.getToken() });
    return this.http.post(reqUrl, formData, { headers: headers })
      .pipe(map(result => { return result }));
  }

  uploadFile(url: string, formData: any) {
    return new Promise<void>((resolve, reject) => {
      this.safeSubscribe(this.upload(url, formData),
        data => {
          FileService.updateMessage(true);
          if (data && data.DomainExecutionErrors) {
            reject({ modelErrors: data.DomainExecutionErrors });
          } else {
            resolve(data);
          }
        },
        error => reject(error));
    });
  }

  delete(url: string, formData: any): Observable<any> {
    const reqUrl = this.rootUrl + url;
    const headers = new HttpHeaders({ 'Authorization': this.authService.getToken() });
    return this.http.post(reqUrl, formData, { headers: headers })
      .pipe(map(result => { return result }));
  }

  deleteFile(url: string, formData: any) {
    return new Promise<void>((resolve, reject) => {
      this.safeSubscribe(this.delete(url, formData),
        data => {
          FileService.updateMessage(true);
          if (data && data.DomainExecutionErrors) {
            reject({ modelErrors: data.DomainExecutionErrors });
          } else {
            resolve(data);
          }
        },
        error => reject(error));
    });
  }

  getdisplayFileName(fileName: string): string {
    const lastIndexu = fileName.lastIndexOf("_");
    const lastIndexd = fileName.lastIndexOf(".");
    const namewithouttime = fileName.substring(0, lastIndexu);
    const fileExtension = fileName.substring(lastIndexd);

    return `${namewithouttime}${fileExtension}`;
  }

  getDateCreated(fileName: string): Date {
    const lastIndexu = fileName.lastIndexOf("_");
    const lastIndexd = fileName.lastIndexOf(".");
    const datestring = fileName.substring(lastIndexu + 1, lastIndexd);
    const year = datestring.substring(0, 4);
    const month = datestring.substring(4, 6);
    const day = datestring.substring(6, 8);
    const h = datestring.substring(8, 10);
    const m = datestring.substring(10, 12);
    const s = datestring.substring(12, 14);

    const dateCreated = moment(moment.utc(new Date(Number(year), Number(month) - 1, Number(day), Number(h), Number(m), Number(s))).toDate()).local().toDate();

    return dateCreated;
  }

  abstract loadDocuments(responderId, pageNumber, pageSize, sortInfo?): Promise<FileUploadResult>;

  abstract getFilteredDocumentTypes(): Promise<any[]>;
}
